import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { fetchDealerConfigSettingsViaShow } from '../services/dealerConfig';
import { DEALER_CONFIG_FETCH_TIMEOUT_MS } from '../types/dealerConfig.types';
import type { DealerConfigSetting, DealerConfigStatus } from '../types/dealerConfig.types';
import { AUTH_LOGOUT_ACTION_TYPE } from '@features/auth/store/actionTypes';

export interface DealerConfigState {
    /** RAW settings per dealer id — never a resolved boolean (plan D4). */
    byDealerId: Record<string, DealerConfigSetting[]>;
    status: Record<string, DealerConfigStatus>;
    /**
     * requestId of the one currently-accepted fetch per dealer id — the
     * stale-completion guard. A completion whose requestId is no longer the
     * recorded one is ignored, which is what stops an in-flight request from the
     * PREVIOUS session repopulating the cache after a reset: the reset wipes this
     * map, so every already-dispatched request becomes unrecognised on arrival.
     */
    requestIdByDealerId: Record<string, string>;
}

const initialState: DealerConfigState = { byDealerId: {}, status: {}, requestIdByDealerId: {} };

// The home dealer id deliberately does NOT live here. It lives on
// `AuthState.dealerId`, written by the authSlice reducers that already handle
// `login.fulfilled` and `refreshToken.fulfilled` — the only wiring that every
// refresh path goes through (plan D8 / G24). Holding it in this slice and
// dispatching it from `useAuth` would set it on an explicit login and nowhere
// else, leaving it null for the rest of the session after any page reload.

/**
 * Fetch one dealer's config settings via the opt-in BREAD read (#789).
 *
 * `condition` is the cache gate: an entry already `loading` or `ready` is never
 * refetched, so the login prefetch and the useDealerConfig() self-heal can both
 * fire for the same dealer within one tick and only one request goes out. An
 * `error` entry IS retried — a transient dealers-service failure must be
 * recoverable without a reload.
 *
 * Rejection is absorbed into an 'error' status. It is never rethrown, because
 * this thunk is dispatched fire-and-forget from the login path and must never be
 * able to fail a login (plan D9). A timeout settles the same way, failing closed
 * to dealer-first only on a first failure; a failed refetch keeps cached settings.
 */
export const fetchDealerConfig = createAsyncThunk<
    { dealerId: string; settings: DealerConfigSetting[] },
    string,
    { state: { dealerConfig: DealerConfigState } }
>(
    'dealerConfig/fetch',
    async (dealerId) => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const bound = new Promise<never>((_, reject) => {
            timer = setTimeout(
                () => reject(new Error('Dealer config request timed out')),
                DEALER_CONFIG_FETCH_TIMEOUT_MS,
            );
        });

        try {
            const settings = await Promise.race([
                fetchDealerConfigSettingsViaShow(dealerId),
                bound,
            ]);
            return { dealerId, settings };
        } finally {
            clearTimeout(timer);
        }
    },
    {
        condition: (dealerId, { getState }) => {
            const status = getState().dealerConfig.status[dealerId];
            return status !== 'loading' && status !== 'ready';
        },
    },
);

const dealerConfigSlice = createSlice({
    name: 'dealerConfig',
    initialState,
    reducers: {
        /**
         * Explicit full reset. The slice is NOT persisted (plan D5), so this only
         * matters within a session — but a second user logging in on the same tab
         * must not inherit the first user's dealer config.
         *
         * This is the reset PRIMITIVE. It is not the logout wiring: logout is
         * handled by the `auth/logout` case below, so no caller has to remember to
         * pair the two.
         */
        clearDealerConfig: () => initialState,

        /**
         * Drop one dealer's cached config. Clearing `status` returns it to 'idle',
         * which useDealerConfig's existing self-heal effect already refetches on.
         */
        invalidateDealerConfig: (state, action: PayloadAction<string>) => {
            delete state.byDealerId[action.payload];
            delete state.status[action.payload];
            delete state.requestIdByDealerId[action.payload];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDealerConfig.pending, (state, action) => {
                state.status[action.meta.arg] = 'loading';
                state.requestIdByDealerId[action.meta.arg] = action.meta.requestId;
            })
            .addCase(fetchDealerConfig.fulfilled, (state, action) => {
                // Stale-completion guard. Without it, a request in flight when the
                // cache was reset (logout / clearDealerConfig) fulfils afterwards and
                // repopulates it with the PREVIOUS user's config, defeating the
                // cross-user reset guarantee above.
                if (state.requestIdByDealerId[action.payload.dealerId] !== action.meta.requestId) {
                    return;
                }
                delete state.requestIdByDealerId[action.payload.dealerId];
                state.byDealerId[action.payload.dealerId] = action.payload.settings;
                state.status[action.payload.dealerId] = 'ready';
            })
            .addCase(fetchDealerConfig.rejected, (state, action) => {
                // Same guard: a stale rejection must not resurrect an 'error' status
                // on a cache that has since been reset.
                if (state.requestIdByDealerId[action.meta.arg] !== action.meta.requestId) {
                    return;
                }
                delete state.requestIdByDealerId[action.meta.arg];
                state.status[action.meta.arg] = 'error';
                // Deliberately leave any previously-cached settings in place: a
                // failed REFETCH must not discard good data. On a first failure
                // there is nothing cached and the resolver reads undefined -> false.
            })
            // CENTRALISED cross-user reset. Every logout path in the app funnels
            // through the authSlice `logout` action — the two useAuth handlers AND
            // the global 401 "Invalid Token" path, which calls
            // `authUtils.handleLogout(store.dispatch)` from the Axios response
            // interceptor and never touches this feature's hooks. Reacting to the
            // action here, rather than asking each caller to also dispatch
            // clearDealerConfig(), is what makes the automatic-logout path safe and
            // keeps any future logout path safe by construction.
            .addCase(AUTH_LOGOUT_ACTION_TYPE, () => initialState);
    },
});

export const { clearDealerConfig, invalidateDealerConfig } = dealerConfigSlice.actions;
export default dealerConfigSlice.reducer;
